import { Box, Chip, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';

interface ChipElement {
  label: string;
  icon?: JSX.Element;
}

export interface CardWithChipsListProps {
  title: string;
  items?: ChipElement[];
  onEdit: () => void;
  titleIcon: ReactNode;
  emptyLabel: string;
  hasAllItems: boolean;
  allItemsLabel: string;
}

export default function CardWithChipsList({ title, items, titleIcon, emptyLabel, allItemsLabel, hasAllItems, onEdit }: CardWithChipsListProps): React.JSX.Element {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        backgroundColor: 'surfaceContainerLow.main',
        borderRadius: 4,
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          flex: 1,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {titleIcon}
          <Typography variant="bodyMedium">
            {title}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {hasAllItems && (
            <Chip label={allItemsLabel} variant="tag" color="surfaceContainerHigh" />
          )}
          {!hasAllItems && (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {items?.length ? (
                items.map((item) => (
                  <Chip key={item.label} label={item.label} icon={item.icon} variant="tag" color="surfaceContainerHigh" />
                ))
              ) : <Chip label={emptyLabel} variant="tag" color="surfaceContainerHigh" />}
            </>
          )}
        </Box>
      </Box>
      <Button
        variant="tonal"
        startIcon={!isSmall && <EditOutlined />}
        onClick={onEdit}
      >
        {isSmall ? <EditOutlined /> : t('common:modify')}
      </Button>
    </Box>
  );
}
